/**
 * Space Reports moderation queue (P4).
 *
 * Lists all `public_space_item_reports` rows grouped by status; admin can
 * uphold (takedown item) or dismiss (clear report only). Both decisions
 * write an append-only `admin.space_report_resolve` audit row inside the
 * resolve transaction on the server.
 */

import type {
  AdminSpaceReportItem,
  PublicSpaceItemReportReason,
  PublicSpaceItemReportStatus,
} from "@hasheeai/types";
import { useCallback, useEffect, useState } from "react";
import {
  type AdminSpaceReportsListFilter,
  fetchAdminSpaceReports,
  resolveAdminSpaceReport,
} from "../../api/admin-space-reports";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";
import { listStyles as s } from "../../styles/list-page";

const PAGE_SIZE = 50;

const STATUS_OPTIONS: ReadonlyArray<AdminSpaceReportsListFilter> = [
  "pending",
  "auto_upheld",
  "upheld",
  "dismissed",
  "all",
];

function formatDate(value: string | null): string {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function statusLabel(value: PublicSpaceItemReportStatus): string {
  return t(`space_reports.status_${value}`);
}

function reasonLabel(value: PublicSpaceItemReportReason): string {
  return t(`space_reports.reason_${value}`);
}

export function SpaceReportsPage(): React.JSX.Element {
  const [status, setStatus] = useState<AdminSpaceReportsListFilter>("pending");
  const [items, setItems] = useState<ReadonlyArray<AdminSpaceReportItem>>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (
      nextStatus: AdminSpaceReportsListFilter,
      nextCursor: string | null,
    ): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetchAdminSpaceReports({
          status: nextStatus,
          cursor: nextCursor,
          limit: PAGE_SIZE,
        });
        setItems((prev) =>
          nextCursor === null ? resp.data : [...prev, ...resp.data],
        );
        setCursor(resp.pagination.next_cursor);
        setHasMore(resp.pagination.has_more);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : t("common.failed_to_load"));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void fetchPage(status, null);
  }, [status, fetchPage]);

  const resolveReport = useCallback(
    async (
      report: AdminSpaceReportItem,
      decision: "upheld" | "dismissed",
    ): Promise<void> => {
      const promptText =
        decision === "upheld"
          ? t("space_reports.confirm_uphold")
          : t("space_reports.confirm_dismiss");
      if (!window.confirm(promptText)) return;
      setResolvingId(report.report_id);
      try {
        const resp = await resolveAdminSpaceReport(report.report_id, {
          decision,
          takedown_reason:
            decision === "upheld" ? `manual_takedown:${report.reason}` : null,
        });
        setItems((prev) =>
          prev.map((row) =>
            row.report_id === report.report_id
              ? {
                  ...row,
                  status: resp.data.status,
                  reviewed_at: new Date().toISOString(),
                  item_taken_down_at:
                    resp.data.item_taken_down || row.item_taken_down_at
                      ? new Date().toISOString()
                      : row.item_taken_down_at,
                }
              : row,
          ),
        );
      } catch (e: unknown) {
        alert(e instanceof Error ? e.message : t("common.failed_to_load"));
      } finally {
        setResolvingId(null);
      }
    },
    [],
  );

  return (
    <div>
      <PageHeader
        title={t("space_reports.title")}
        description={t("space_reports.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: items.length }]} />
      <div style={s.filterBar}>
        {STATUS_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setStatus(option)}
            style={{
              ...s.searchBtn,
              ...(option === status ? activeBtn : inactiveBtn),
            }}
          >
            {t(`space_reports.filter_${option}`)}
          </button>
        ))}
      </div>
      {error && <p style={s.error}>{error}</p>}
      {loading && items.length === 0 ? (
        <p style={s.loading}>{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <p style={s.empty}>{t("space_reports.empty")}</p>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>{t("space_reports.col_item")}</th>
                <th style={s.th}>{t("space_reports.col_reason")}</th>
                <th style={s.th}>{t("space_reports.col_status")}</th>
                <th style={s.th}>{t("space_reports.col_reported_at")}</th>
                <th style={s.th}>{t("space_reports.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <SpaceReportRow
                  key={row.report_id}
                  row={row}
                  busy={resolvingId === row.report_id}
                  onUphold={() => {
                    void resolveReport(row, "upheld");
                  }}
                  onDismiss={() => {
                    void resolveReport(row, "dismissed");
                  }}
                />
              ))}
            </tbody>
          </table>
          {hasMore && (
            <div style={s.pagination}>
              <button
                type="button"
                onClick={() => void fetchPage(status, cursor)}
                style={s.searchBtn}
              >
                {t("common.next")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SpaceReportRow(props: {
  readonly row: AdminSpaceReportItem;
  readonly busy: boolean;
  readonly onUphold: () => void;
  readonly onDismiss: () => void;
}): React.JSX.Element {
  const { row } = props;
  const takenDown = !!row.item_taken_down_at;
  return (
    <tr>
      <td style={s.td}>
        <div style={{ fontWeight: 600 }}>{row.item_title}</div>
        <div style={{ fontSize: 11, opacity: 0.6 }}>
          {row.item_id.slice(0, 8)}
        </div>
        {takenDown && (
          <div style={{ fontSize: 11, color: "var(--color-danger)" }}>
            {t("space_reports.taken_down_at")}{" "}
            {formatDate(row.item_taken_down_at)}
          </div>
        )}
      </td>
      <td style={s.td}>{reasonLabel(row.reason)}</td>
      <td style={s.td}>{statusLabel(row.status)}</td>
      <td style={s.td}>{formatDate(row.created_at)}</td>
      <td style={s.td}>
        {row.status === "pending" ? (
          <span style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              disabled={props.busy}
              onClick={props.onUphold}
              style={{ ...s.searchBtn, ...upholdBtn }}
            >
              {t("space_reports.action_uphold")}
            </button>
            <button
              type="button"
              disabled={props.busy}
              onClick={props.onDismiss}
              style={{ ...s.searchBtn, ...dismissBtn }}
            >
              {t("space_reports.action_dismiss")}
            </button>
          </span>
        ) : (
          <span style={{ opacity: 0.6 }}>{statusLabel(row.status)}</span>
        )}
      </td>
    </tr>
  );
}

const activeBtn: React.CSSProperties = {
  background: "var(--color-bg-hover)",
  fontWeight: 700,
};
const inactiveBtn: React.CSSProperties = { background: "transparent" };
const upholdBtn: React.CSSProperties = {
  background: "var(--color-danger)",
  color: "white",
};
const dismissBtn: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--color-border)",
};
